import { useState, useCallback, useRef } from "react";

import { config } from "../../config";
import { useHttpClient } from "../Hooks/http-hook";
import dedupeStoreFetch from "../util/dedupe-store-fetch";

const useFeedbacks = () => {
    const [feedbacks, setFeedbacks] = useState();
    const [isUpdatedFeedbacks, setIsUpdatedFeedbacks] = useState(false);
    const [HideFeedbacksSkeleton, setHideFeedbacksSkeleton] = useState(false);
    const [feedbacksStoreUsername, setFeedbacksStoreUsername] = useState('');
    const { error, sendRequest, clearError } = useHttpClient();
    const cacheRef = useRef({
        isUpdatedFeedbacks: false,
        feedbacksStoreUsername: '',
        feedbacks: null,
    });
    const pendingFetchesRef = useRef(new Map());

    cacheRef.current = {
        isUpdatedFeedbacks,
        feedbacksStoreUsername,
        feedbacks,
    };

    const updateFeedbacks = (newFeedbacks) => {
        setFeedbacks(newFeedbacks);
        setIsUpdatedFeedbacks(true);
    };

    const isEmptyFeedbacks = useCallback(() => {
        return feedbacks?.length > 0;
    }, [feedbacks]);

    const fetchFeedbacks = useCallback(async (storeUsername, { force = false } = {}) => {
        return dedupeStoreFetch({
            key: storeUsername,
            force,
            cacheRef,
            pendingRef: pendingFetchesRef,
            isCacheHit: (cache) => (
                cache.isUpdatedFeedbacks && cache.feedbacksStoreUsername === storeUsername
            ),
            getCachedValue: (cache) => cache.feedbacks,
            runRequest: async () => {
                try {
                    const responseData = await sendRequest(
                        `${config.API_URL}/feedbacks/${storeUsername}`
                    );
                    clearError();
                    updateFeedbacks(responseData);
                    setFeedbacksStoreUsername(storeUsername);
                    return responseData;
                } catch (err) {
                    setIsUpdatedFeedbacks(true);
                    setFeedbacksStoreUsername(storeUsername);
                    console.error(err);
                    return null;
                }
            },
        });
    }, [sendRequest, clearError]);

    const adminFetchFeedbacks = useCallback(async (status, auth, storeUsername) => {
        try {
            const responseData = await sendRequest(
                `${config.API_URL}/feedbacks/${status}/${storeUsername}`,
                'GET',
                null,
                {
                    Authorization: 'Bearer ' + auth.token
                }
            );
            clearError();
            updateFeedbacks(responseData);
            setFeedbacksStoreUsername(storeUsername);
            pendingFetchesRef.current.delete(storeUsername);
        } catch (err) {
            setIsUpdatedFeedbacks(true);
            throw err
        }
    }, [sendRequest, clearError]);

    const FeedbackDeletedHandler = useCallback((deletedFeedbackId) => {
        setFeedbacks(prevFeedbacks => prevFeedbacks.filter(feedback => feedback._id !== deletedFeedbackId));
    }, [setFeedbacks]);

    const reloadFeedbacks = useCallback(
        (storeUsername) => fetchFeedbacks(storeUsername, { force: true }),
        [fetchFeedbacks]
    );

    const removeFeedbacks = useCallback(() => {
        pendingFetchesRef.current.clear();
        setFeedbacks(null);
        setFeedbacksStoreUsername('');
        setIsUpdatedFeedbacks(false);
        setHideFeedbacksSkeleton(false);
    }, []);

    return {
        feedbacks,
        updateFeedbacks,
        isUpdatedFeedbacks, 
        fetchFeedbacks,
        reloadFeedbacks,
        HideFeedbacksSkeleton,
        isEmptyFeedbacks,
        FeedbackDeletedHandler,
        FeedbacksError: error,
        adminFetchFeedbacks,
        setHideFeedbacksSkeleton,
        removeFeedbacks,
    };
}

export default useFeedbacks;
